"use client";

import { useCallback } from "react";

import type { CandidateEditableProfileDto, JobItemDto, ResumeUploadSource } from "@artha/api-contracts";

import { requestCandidateVerification, verifyCandidateCode } from "../services/personalizationClient";
import { requestCandidateSignInLink } from "../services/contactVerificationClient";

import { ANALYTICS_EVENTS } from "@/lib/analytics/events";

import { useCandidatePersonalizationState, hasPersonalizedProfile } from "./useCandidatePersonalizationState";
import { useCandidateResumePersonalization } from "./resume/useCandidateResumePersonalization";
export const useCandidatePersonalization = (creatorId?: string) => {
  const state = useCandidatePersonalizationState(creatorId);
  const { t, posthog, identity, editableProfile, setEditableProfile, isAuthenticated, hasResume, isSessionLoading, isUploading, isRequestingCode, setIsRequestingCode, isVerifying, setIsVerifying, isLoggingOut, verificationMode, setVerificationMode, maskedEmail, setMaskedEmail, jobsRefreshVersion, error, setError, capturePersonalizationEvent, applySession, getPosthogDistinctId, refreshSession, restoreProfileDraft, logout, getAlternativeJobs } = state;
 const uploadResume = useCandidateResumePersonalization(creatorId, state);

  const requestProfileVerificationResult = useCallback(
    async (
      profile: CandidateEditableProfileDto,
      options?: {
        source?: ResumeUploadSource;
        creatorId?: string;
        job?: Pick<JobItemDto, "id" | "ref_id">;
        fitAttemptId?: string;
        skipFit?: boolean;
      },
    ) => {
      if (!identity.externalId) {
        throw new Error(t("errors.uploadFirst"));
      }
      setIsRequestingCode(true);
      setError(null);
      try {
        const result = await requestCandidateVerification({
          externalId: identity.externalId,
          posthogDistinctId: getPosthogDistinctId(),
          editableProfile: profile,
          creatorId: options?.creatorId ?? creatorId ?? undefined,
          source: options?.source,
          jobId: options?.job?.id,
          refId: options?.job?.ref_id || undefined,
          fitAttemptId: options?.fitAttemptId,
          skipFit: options?.skipFit,
        });
        setEditableProfile(profile);
        setVerificationMode("profile");
        setMaskedEmail(result.maskedEmail);
        if (!result.verificationRequired) {
          applySession(result, true);
        }
        if (result.applyGate?.status === "no_match") {
          capturePersonalizationEvent(ANALYTICS_EVENTS.AUD_JOB_FIT_NO_MATCH, {
            job_id: options?.job?.id,
            ref_id: options?.job?.ref_id || undefined,
          });
        }
        return result;
      } catch (requestError) {
        const message =
          requestError instanceof Error
            ? requestError.message
            : t("errors.verificationSendFailed");
        setError(message);
        throw requestError;
      } finally {
        setIsRequestingCode(false);
      }
    },
    [
      applySession,
      capturePersonalizationEvent,
      creatorId,
      getPosthogDistinctId,
      identity.externalId,
      t,
    ],
  );

  const requestProfileVerification = useCallback(
    async (
      profile: CandidateEditableProfileDto,
      options?: { source?: ResumeUploadSource; creatorId?: string },
    ) =>
      (await requestProfileVerificationResult(profile, options))
        .verificationRequired,
    [requestProfileVerificationResult],
  );

  const requestSignIn = useCallback(
    async (
      email: string,
      options?: { source?: ResumeUploadSource; creatorId?: string },
    ) => {
      setIsRequestingCode(true);
      setError(null);
      setVerificationMode("login");
      try {
        const source = options?.source;
        const portalId = options?.creatorId ?? creatorId;
        const result =
          (source === "jobs_page" || source === "talent_network") && portalId
            ? await requestCandidateSignInLink({
                email,
                creatorId: portalId,
                source,
              })
            : await requestCandidateVerification({
                email,
                creatorId: portalId,
                source,
              });
        setMaskedEmail(result.maskedEmail);
        if (!result.verificationRequired) {
          applySession(result, true);
          capturePersonalizationEvent(ANALYTICS_EVENTS.AUD_RESUME_LOGIN, {
            has_resume: result.hasResume,
            email: (result.editableProfile?.email || email)
              .trim()
              .toLowerCase(),
          });
        }
        return result;
      } catch (requestError) {
        const message =
          requestError instanceof Error
            ? requestError.message
            : t("errors.verificationSendFailed");
        setError(message);
        throw requestError;
      } finally {
        setIsRequestingCode(false);
      }
    },
    [applySession, capturePersonalizationEvent, creatorId, t],
  );

  const verifyCode = useCallback(
    async (email: string, code: string, fitAttemptId?: string) => {
      setIsVerifying(true);
      setError(null);
      try {
        const posthogDistinctId = getPosthogDistinctId();
        const result = await verifyCandidateCode({
          email,
          code,
          posthogDistinctId,
          ...(verificationMode === "profile" && identity.externalId
            ? {
                externalId: identity.externalId,
              }
            : {}),
          ...(fitAttemptId ? { fitAttemptId } : {}),
        });
        applySession(result, true);
        capturePersonalizationEvent(ANALYTICS_EVENTS.AUD_RESUME_VERIFICATION, {
          mode: verificationMode,
          has_resume: result.hasResume,
        });
        if (verificationMode === "login") {
          capturePersonalizationEvent(ANALYTICS_EVENTS.AUD_RESUME_LOGIN, {
            has_resume: result.hasResume,
            email: (result.editableProfile?.email || email)
              .trim()
              .toLowerCase(),
          });
        }
        return result;
      } catch (verificationError) {
        const message =
          verificationError instanceof Error
            ? verificationError.message
            : t("errors.verificationFailed");
        setError(message);
        throw verificationError;
      } finally {
        setIsVerifying(false);
      }
    },
    [
      applySession,
      capturePersonalizationEvent,
      getPosthogDistinctId,
      identity.externalId,
      t,
      verificationMode,
    ],
  );

  return {
    creatorId,
    externalId: isAuthenticated ? identity.externalId : null,
    identityExternalId: identity.externalId,
    posthogDistinctId: posthog?.get_distinct_id?.() || null,
    isAuthenticated,
    isSessionLoading,
    isPersonalized:
      isAuthenticated &&
      (hasResume || hasPersonalizedProfile(editableProfile)),
    hasResume,
    candidatePreferences: isAuthenticated
      ? identity.candidatePreferences
      : null,
    editableProfile,
    maskedEmail,
    verificationMode,
    isUploading,
    isRequestingCode,
    isVerifying,
    isLoggingOut,
    jobsRefreshVersion,
    error,
    refreshSession,
    restoreProfileDraft,
    uploadResume,
    requestProfileVerification,
    requestApplyFitVerification: requestProfileVerificationResult,
    requestSignIn,
    verifyCode,
    getAlternativeJobs,
    logout,
  };
};

export type { CandidatePersonalizationController } from "../personalization.types";
